import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";

import { usePartnerInfo } from "@utils";

import { Modal } from "@ui/components";
import {
  Button,
  Icon,
  ImageComponent,
  Subtitle,
  Text,
  Title,
} from "@ui/elements";
import { Checkbox } from "@ui/forms";
import { Stack } from "@ui/layout";

import "./TelegramJoinModal.scss";

import { CommonModalProps } from "@components";

interface TelegramJoinModalProps extends Omit<CommonModalProps, "onClose"> {
  onJoinTelegram?: (dismissedPermanently: boolean) => void; // Callback when user clicks "Join Telegram"
  onClose: (dismissedPermanently: boolean) => void; // Callback for "Never show again"
}

export const TelegramJoinModal: FC<TelegramJoinModalProps> = ({
  isOpen,
  onClose,
  onJoinTelegram,
}) => {
  const { t } = useTranslation();
  const [dismissedPermanently, setDismissedPermanently] = useState(false);
  const partnerInfo = usePartnerInfo();

  const handleDismiss = () => {
    onClose(dismissedPermanently);
  };

  const handleJoin = () => {
    if (onJoinTelegram) {
      onJoinTelegram(dismissedPermanently);
    }
  };

  return (
    <Modal
      isActive={isOpen}
      onClose={handleDismiss}
      className="telegram-modal"
      size="large"
      header={
        <Stack direction="column" align="start" gap="xs">
          <Stack direction="row" align="center" gap="md">
            <Icon icon="TelegramLogo" size="medium" />
            <Title size={4}>{t("telegramJoin.headerTitle")}</Title>
          </Stack>
        </Stack>
      }
      style={{ zIndex: 100 }}
      footer={
        <Stack justify="between" align="center" isFullwidth>
          <Stack direction="row" align="center" gap="sm">
            <Button variant="light" onClick={handleDismiss}>
              {t("telegramJoin.dismiss")}
            </Button>
            <Checkbox
              checked={dismissedPermanently}
              onChange={(e) => setDismissedPermanently(e.target.checked)}
            >
              {t("telegramJoin.neverShow")}
            </Checkbox>
          </Stack>
          <Button
            as="link"
            href={partnerInfo.contact.telegram}
            target="_blank"
            className="telegram-join-button"
            onClick={handleJoin}
          >
            <Icon icon="TelegramLogo" />
            <span>{t("telegramJoin.join")}</span>
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" gap="md" className="telegram-modal-content">
        <div className="telegram-image-wrapper">
          <ImageComponent src="telegram-screenshot-mobile.png" />
        </div>
        <Stack direction="column" gap="xl" className="telegram-join-content">
          <Stack direction="column" gap="sm">
            <Stack direction="column" gap="xxs">
              <Title size={4}>{t("telegramJoin.modalTitle")}</Title>
              <Subtitle size={5}>{t("telegramJoin.modalSubtitle")}</Subtitle>
            </Stack>
            <div className="content">
              <blockquote>{t("telegramJoin.blockquote")}</blockquote>
            </div>
          </Stack>
          <Stack direction="column" gap="sm">
            <Title size={5}>{t("telegramJoin.whyJoinTitle")}</Title>
            <ul className="benefits-list">
              <li className="is-flex is-gap-0.5">
                <Icon
                  icon="CheckCircle"
                  className="has-text-success"
                  weight="fill"
                />{" "}
                {t("telegramJoin.benefits.realTimePredictions")}
              </li>
              <li className="is-flex is-gap-0.5">
                <Icon
                  icon="CheckCircle"
                  className="has-text-success"
                  weight="fill"
                />{" "}
                {t("telegramJoin.benefits.communityTips")}
              </li>
              <li className="is-flex is-gap-0.5">
                <Icon
                  icon="CheckCircle"
                  className="has-text-success"
                  weight="fill"
                />{" "}
                {t("telegramJoin.benefits.timelyUpdates")}
              </li>
              <li className="is-flex is-gap-0.5">
                <Icon
                  icon="CheckCircle"
                  className="has-text-success"
                  weight="fill"
                />{" "}
                {t("telegramJoin.benefits.exclusiveStrategies")}
              </li>
              <li className="is-flex is-gap-0.5">
                <Icon
                  icon="CheckCircle"
                  className="has-text-success"
                  weight="fill"
                />{" "}
                {t("telegramJoin.benefits.predictionGames")}
              </li>
            </ul>
          </Stack>
          <Text>{t("telegramJoin.joinText")}</Text>
        </Stack>
      </Stack>
    </Modal>
  );
};
